import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { STOP_OPERATOR } from 'lib/api/mutations';
import { getMachine } from 'lib/selectors/getMachine';
import { getOperatorIsLoggingOut } from 'lib/selectors/getOperatorIsLoggingOut';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { getIsClockOutModalSkipped } from 'lib/selectors/getIsClockOutModalSkipped';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import {
  actionSetRecentOperatorRuns,
  actionSetOperatorIsLoggingOut,
  actionOpenClockOutModal,
} from 'lib/actions';

const useOperatorLogout = (onComplete = () => {}) => {
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const isLoggingOut = useSelector(getOperatorIsLoggingOut);
  const currentOperator = useSelector(getCurrentOperator);
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);
  const isClockOutModalSkipped = useSelector(getIsClockOutModalSkipped);

  const [stopOperator, { loading }] = useMutation(STOP_OPERATOR, {
    fetchPolicy: 'no-cache',
    onCompleted: async ({ stopOperatorResponse }) => {
      if (stopOperatorResponse) {
        const {
          machine: localMachine,
          operator,
          ...operatorRun
        } = stopOperatorResponse;
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: localMachine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));

        // Open clockOut modal if either clock out flow or shift handover is enabled
        if (
          (clockOutFlowEnabled || shiftHandoverEnabled) &&
          !isClockOutModalSkipped(currentOperator?.erpId)
        ) {
          // Use current operator from store (they're the one signing out)
          dispatch(actionOpenClockOutModal(currentOperator?.erpId));
        }
      }

      onComplete();
    },
    onError: () => {
      onComplete();
    },
  });

  useEffect(() => {
    dispatch(actionSetOperatorIsLoggingOut(loading));
    return () => {
      dispatch(actionSetOperatorIsLoggingOut(false));
    };
  }, [dispatch, loading]);

  return async () => {
    if (isLoggingOut || loading) {
      return;
    }
    await stopOperator({
      variables: {
        input: {
          machineId: machine.id,
        },
      },
    });
  };
};

export default useOperatorLogout;
